import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { getActiveActivityProfiles } from "PFCore/helpers/get_active_activity_profiles";
import { getProfileName } from "PFCore/helpers/profile";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useReviewerCreate, useReviewerDelete } from "PFCore/hooks/queries/reviewers";
import { Activity, ActivityProfile, ActivityProfileType } from "PFTypes";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { UseAddActivityProfilesReturn } from "./activity_profiles.types";
import {
  AddActivityProfilesModalContent,
  AddActivityProfilesModalContentProps
} from "./add_activity_profiles_modal_content";

type UseAddActivityProfilesProps = {
  activity: Activity;
  isUnbiasedEnabled: boolean;
};

export const useAddReviewersModal = ({
  activity,
  isUnbiasedEnabled
}: UseAddActivityProfilesProps): UseAddActivityProfilesReturn => {
  const { showGrowl } = useGrowlStoreActions();
  const { t } = useTranslation("activities", { keyPrefix: "show.shortlist.review" });
  const { getFirstError } = useResponseErrors();
  const activityReviewers = getActiveActivityProfiles(activity?.reviewers || []);

  const { mutateAsync: createReviewer } = useReviewerCreate();
  const { mutate: deleteReviewer } = useReviewerDelete(activity?.id || 0);

  const [newProfiles, setNewProfiles] = useState<AddActivityProfilesModalContentProps["newProfiles"]>([]);

  const description = (
    <div>
      <Trans i18nKey="addReviewersDescription" t={t} components={[<strong key="reviewer-strong-tag" />]} />
      {isUnbiasedEnabled && <div style={{ marginTop: 10 }}>{t("addReviewersDescriptionUnbiased")}</div>}
    </div>
  );

  const handleReset = () => {
    setNewProfiles([]);
  };

  const handleRemove = (activityProfile: ActivityProfile) => {
    const reviewer = activityReviewers.find(({ profile }) => activityProfile.profile.id === profile.id);
    if (!reviewer?.id) {
      return null;
    }

    deleteReviewer(Number(reviewer.id), {
      onSuccess: () =>
        showGrowl({
          message: t("profileRemovedReviewer", {
            profileName: getProfileName(reviewer.profile)
          }),
          kind: "success"
        })
    });
  };

  const handleSubmit = () =>
    newProfiles.forEach((profile) =>
      createReviewer({
        profileId: profile.id,
        activityId: activity.id,
        unbiased: profile.unbiased || false
      })
        .then(() =>
          showGrowl({
            message: t("requestReviewSuccess", { profileName: getProfileName(profile) }),
            kind: "success"
          })
        )
        .catch((response) => {
          const error = getFirstError(response);
          showGrowl({
            kind: "error",
            message: error || t("requestReviewError", { profileName: getProfileName(profile) })
          });
        })
    );

  const getModalContent = () => (
    <AddActivityProfilesModalContent
      activity={activity}
      profiles={activityReviewers}
      onRemove={handleRemove}
      description={description}
      showUnbiasedToggle
      newProfiles={newProfiles}
      onNewProfilesChange={setNewProfiles}
      activityProfileType={ActivityProfileType.Reviewer}
      selectQaId="ManageParticipants.ReviewersSelect"
    />
  );

  return { getModalContent, handleSubmit, enableSubmit: newProfiles.length > 0, handleReset };
};
